// Basic
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
// Libs
// Store & actions
import useAssetsStore from '@/app/store/assets';
import useCustomPayStore from '@/app/store/customPay';
import useDeviceSizeStore from '@/app/store/deviceSize';
import useSessionStore from '@/app/store/session';
import actionsPreLogin from '../api/actionsPreLogin';
// Components
import Logo, { LogoCustomPay } from '@/common-utils/components/renders/logo/Logo';
import LocalePicker from '@/common-utils/components/renders/localePicker/LocalePicker';
import Versions, { VersionsCustomPay } from '@/common-utils/components/renders/versions/Versions';
import PreLoginForm from './form/PreLoginForm';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import defaultBackground from '../../../../../resource/images/loginMo-bg.png';
import defaultBackgroundSmall from '../../../../../resource/images/loginMo-smallBg.jpg';
import defaultBackgroundPayview from '../../../../../resource/images/login-background.png';
import { useToast } from '@/common-utils/hooks/use-toast';
import RouteUrls from '@/app/root/enums/RouteUrls';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
// Style
import './PreLogin.scss';
// Lang
import { nsErrors } from '@/app/i18n/namespaces';

/**
 * @returns {JSX.Element} PreLogin
 */
function PreLogin() {
    // Store
    const assets = useAssetsStore((s) => s.assets);
    const isCustomPay = useCustomPayStore((s) => s.isCustomPay);
    const isSmallDevice = useDeviceSizeStore(s => s.isSmallDevice);
    const session = useSessionStore((s) => s.session);

    // Toast
    const { toast } = useToast();

    // Get the query string
    const location = useLocation();
    const queryString = location.search;

    // API calls
    const { token } = extractQueriesFromUrl(queryString);
    const { data, error, isLoading } = actionsPreLogin.fetchToken(token);

    useEffect(() => {
        const errorCode = error?.response?.data?.errorCode || error?.code;
        if (!isLoading && errorCode) {
            toast({
                description: <Entity namespace={nsErrors} entity={`api.${errorCode}`} />,
                variant: 'danger'
            });
        }
    }, [error, isLoading]);

    if (session) {
        return <Navigate to={RouteUrls.HOME.url} />;
    }

    if (error) {
        return <Navigate to={RouteUrls.LOGIN.url} />;
    }

    const getBackground = () => {
        // get small background if exist and small device detected
        if (assets?.backgroundSmall && isSmallDevice) {
            return assets?.backgroundSmall;
        }
        if (assets?.background) {
            return assets?.background;
        }
        // fallback to avoid no background
        if (!assets?.background && isCustomPay) {
            return defaultBackgroundPayview;
        }
        if (!assets?.backgroundSmall && isSmallDevice && !isCustomPay) {
            return defaultBackgroundSmall;
        }
        return defaultBackground;
    };

    const getStyle = (backgroundImageUrl) => {
        return {
            backgroundImage: isSmallDevice ? '' : `url('${backgroundImageUrl}')`,
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            backgroundPosition: '33% center',
            backgroundSize: 'cover'
        };
    };

    // Render
    return (
        <div className={`prelogin-page${isSmallDevice ? ' small-login-page' : ''}`} style={getStyle(getBackground())}>
            <div className={`prelogin-page-overlay${isSmallDevice ? ' small-device' : ''}`}>
                <div className='header'>
                    <LocalePicker />
                    {isCustomPay ?
                        <LogoCustomPay assets={assets} className={`icon-container${isSmallDevice ? ' small-device' : ''}`} />
                        : <Logo assets={assets} className={`icon-container${isSmallDevice ? ' small-device' : ''}`} />
                    }
                </div>
                <div className='content'>
                    {isLoading &&
                        <Spinner className='mt-6' />
                    }
                    {!isLoading && data &&
                        (
                            <PreLoginForm token={data} />
                        )
                    }
                </div>
                {isCustomPay ? <VersionsCustomPay /> : <Versions />}
            </div>
        </div >
    );
}

export default PreLogin;